import { useEffect, useRef, useState } from "react";
import { LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { materialOutward, customerAPI, rawMaterials, Unit } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import ConfirmDeleteModal from "../../common/DeleteModel";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import { formatDate } from "../../utlis/formateDate";
import { RegistryHeader, Button as SharedButton } from "../../common/Shared";

import MaterialOutwardForm from "./MaterialOutwardForm";

const MaterialOutward = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId || 1;
  const today = new Date().toISOString()?.split("T")[0];
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    outward_No: 0,
    outward_Date: today,
    customer_Id: "",
    notes: "",
    details: [{
      trn_No: 0,
      outward_No: 0,
      inward_No: 0,
      material_Id: 0,
      quantity: "",
      unit_Id: "",
      rate: "",
      amount: 0,
      gsT_Per: 18.00,
      gsT_Amount: 0,
      final_Amount: 0,
      created_By: userId,
      modified_By: userId
    }],
    created_By: userId,
  });

  const [loading, setLoading] = useState(false);
  const [isMaterialOutwardData, setIsMaterialOutwardData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCustomerData, setIsCustomerData] = useState([]);
  const [isRawMaterialData, setIsRawMaterialData] = useState([]);
  const [isUnitData, setIsUnitData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const tableScrollRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);

  const fetchOutwardData = async () => {
    if (isFetchingRef.current) return;
    setLoading(true);
    try {
      const payload = {
        pageNumber: currentPage,
        pageSize: pageSize,
        fromDate: null,
        toDate: null,
        supplierId: 0,
        is_Dispatch: 0
      };
      const res = await materialOutward.getAllWithPagination(payload);
      if (currentPage === 1) {
        setIsMaterialOutwardData(res.items || []);
      } else {
        setIsMaterialOutwardData((prev) => [...prev, ...(res.items || [])]);
      }

      if (!res.items || res.items.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      isFetchingRef.current = false;
    } catch (error) {
      notify("Error fetching material outward data", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 120) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [rawMaterialRes, customerRes, unitRes] = await Promise.all([
          rawMaterials.getAll(),
          customerAPI.getAll(),
          Unit.getAll(),
        ]);

        setIsRawMaterialData(
          Array.isArray(rawMaterialRes)
            ? rawMaterialRes
            : rawMaterialRes?.data || [],
        );

        const customerArray = Array.isArray(customerRes?.data)
          ? customerRes.data
          : Array.isArray(customerRes) ? customerRes : [];
        setIsCustomerData(customerArray);

        setIsUnitData(Array.isArray(unitRes) ? unitRes : unitRes?.data || []);
      } catch (error) {
        notify("Error fetching dropdown data", "error");
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    fetchOutwardData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(isMaterialOutwardData);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = isMaterialOutwardData.filter((d) => {
        return (
          d.customer_Name?.toLowerCase().includes(query) ||
          d.notes?.toLowerCase().includes(query)
        );
      });
      setFilteredData(filtered);
    }
  }, [searchQuery, isMaterialOutwardData]);

  const handleAdd = () => {
    setIsEditing(false);
    setFormData({
      outward_No: 0,
      outward_Date: today,
      customer_Id: "",
      notes: "",
      total_Amount: 0,
      total_GST_Amount: 0,
      total_Final_Amount: 0,
      details: [{
        trn_No: 0,
        outward_No: 0,
        inward_No: 0,
        material_Id: 0,
        quantity: "",
        unit_Id: "",
        rate: "",
        amount: 0,
        gsT_Per: 18.00,
        gsT_Amount: 0,
        final_Amount: 0,
        created_By: userId,
        modified_By: userId
      }],
      created_By: userId,
    });
    setIsModelOpen(true);
  };

  const handleEdit = async (id) => {
    setLoading(true);
    try {
      const data = await materialOutward.getById(id);

      if (data) {
        setFormData({
          outward_No: data.outward_No,
          outward_Date: data.outward_Date?.split("T")[0],
          customer_Id: data.customer_Id,
          notes: data.notes,
          total_Amount: data.total_Amount || 0,
          total_GST_Amount: data.total_GST_Amount || 0,
          total_Final_Amount: data.total_Final_Amount || 0,
          details: data.details || [],
          modified_By: userId,
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch outward details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await materialOutward.deleteAll(selectedId);
      notify("Deleted successfully", "success");
      // Reset to page 1 to refresh list correctly
      if (currentPage === 1) {
        fetchOutwardData();
      } else {
        setCurrentPage(1);
      }
    } catch (error) {
      notify("Failed to delete record", "error");
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleSubmit = async (payload, resetOnly = false) => {
    setIsSaving(true);
    try {
      await materialOutward.saveOrUpdate(payload);
      notify(
        isEditing ? "Updated successfully" : "Added successfully",
        "success",
      );

      if (currentPage === 1) {
        fetchOutwardData();
      } else {
        setCurrentPage(1);
      }

      if (resetOnly) {
        setFormData({
          outward_No: 0,
          outward_Date: today,
          customer_Id: "",
          notes: "",
          details: [{
            trn_No: 0,
            inward_No: 0,
            material_Id: 0,
            quantity: "",
            unit_Id: "",
            rate: "",
            amount: 0,
            gsT_Per: 18.00,
            gsT_Amount: 0,
            final_Amount: 0,
            created_By: userId,
            modified_By: userId
          }],
          created_By: userId,
        });
      } else {
        setIsModelOpen(false);
        setFormData({
          outward_No: 0,
          outward_Date: today,
          customer_Id: "",
          notes: "",
          details: [{
            trn_No: 0,
            inward_No: 0,
            material_Id: 0,
            quantity: "",
            unit_Id: "",
            rate: "",
            amount: 0,
            gsT_Per: 18.00,
            gsT_Amount: 0,
            final_Amount: 0,
            created_By: userId,
            modified_By: userId
          }],
          created_By: userId,
        });
        setIsEditing(false);
      }
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  if (isModelOpen) {
    return (
      <MaterialOutwardForm
        formData={formData}
        isEditing={isEditing}
        isSaving={isSaving}
        onSave={handleSubmit}
        onCancel={() => {
          setIsModelOpen(false);
          setFormData({
            outward_No: 0,
            outward_Date: today,
            customer_Id: "",
            notes: "",
            details: [{
              trn_No: 0,
              outward_No: 0,
              inward_No: 0,
              material_Id: 0,
              quantity: "",
              unit_Id: "",
              rate: "",
              amount: 0,
              gsT_Per: 18.00,
              gsT_Amount: 0,
              final_Amount: 0,
              created_By: userId,
              modified_By: userId
            }],
            created_By: userId,
          });
          setIsEditing(false);
        }}
        isCustomerData={isCustomerData}
        isRawMaterialData={isRawMaterialData}
        isUnitData={isUnitData}
        userId={userId}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-white font-plus relative">
      {loading && <Loader centered />}
      <RegistryHeader title="Material Outward">
        <div className="relative w-full sm:w-80 group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-primary">
            <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
          </div>
          <input
            type="text"
            placeholder="Search by CUSTOMER / NOTES..."
            className="block w-full pl-12 pr-10 py-2 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-primary focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
            >
              <MdClose size={18} />
            </button>
          )}
        </div>

        <div>
          <SharedButton
            onClick={handleAdd}
            icon={HiOutlinePlus}
            className="whitespace-nowrap"
            loading={loading && currentPage === 1}
          >
            Add Material Outward
          </SharedButton>
        </div>
      </RegistryHeader>

      <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col">
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-gray-200 bg-white flex-1 "
        >
          <div className="min-w-max">
            <Table
              columns={[
                { key: "outward_No", label: "OUTWARD NO", align: "center" },
                {
                  key: "outward_Date",
                  label: "DATE",
                  align: "center",
                  render: (value) => formatDate(value),
                },
                { key: "customer_Name", label: "CUSTOMER", align: "center" },
                { key: "total_Amount", label: "AMOUNT", align: "center" },
                { key: "total_GST_Amount", label: "GST AMOUNT", align: "center" },
                { key: "total_Final_Amount", label: "FINAL AMOUNT", align: "center" },
                { key: "notes", label: "REMARK", align: "center" },
              ]}
              data={filteredData}
              onEdit={(row) => handleEdit(row.outward_No)}
              onDelete={(row) => handleDelete(row.outward_No)}
              tableClassName="text-[12px] font-bold"
            />
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </div>
  );
};

export default MaterialOutward;
